import React, { useEffect, useMemo, useState } from 'react'
import 'react-datepicker/dist/react-datepicker.css'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import {
  AppointmentData,
  selectSearchValue,
  selectSelectedAppointment,
  selectSelectedAppointmentDate,
  setSelectedAppointment,
  setSelectedDate,
  updateAppointmentAsync,
} from './appointmentsSlice'
import CancelModal from './components/CancelModal'
import AppointmentsFilter from './components/AppointmentsFilter'
import AppointmentsTable from './components/AppointmentsTable'
import KeycloakService from '../../utils/keycloakService'
import { APPOINTMENTS_WRITE } from '../../utils/roles'
import { selectSelectedLocationId } from '../location/locationSlice'
import Slots from '../slots/Slots'
import Scheduler from '../scheduler/Scheduler'
import { appointments, emptyString } from '../Radiology/Constants/constants'
import { formatData } from './Utils'
import {
  ArchiveAppointment,
  deleteAppointment,
  getAppointments,
  getAppointmentsBySlotAndDateService,
} from '../../services/AppointmentServices'
import { toast } from 'react-toastify'
import DatePicker from 'react-datepicker'
import DropdownMenu from '../../components/DropdownMenu/DropdownMenu'
import Calendar from '../../assets/images/calendar.png'
import { exportExcel } from '../../utils/hooks/Utils'
import { dropDownMenuKeys, noDataMessage } from '../../components/constants'
import { dateFilterKey, dateFilterValue } from './models'
import { fetchDepartments } from '../../services/referralService'
const Appointments = () => {
  const selectedId = useAppSelector(selectSelectedAppointment)
  const selectedDate = useAppSelector(selectSelectedAppointmentDate)
  const startDate = useMemo(() => new Date(selectedDate), [selectedDate])
  const selectedLocation = useAppSelector(selectSelectedLocationId)
  const dispatch = useAppDispatch()
  const [selectedStatus, setSelectedStatus] = useState<string>('')
  const [showModal, setShowModal] = useState(false)
  const [showList] = useState('')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(100)
  const [totalCount, setTotalCount] = useState(0)
  const [appointmentsData, setAppointmentsData] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const searchValue = useAppSelector(selectSearchValue)
  const [selectId, setSelectId] = useState<string | number | null>(null)
  const [reload, setReload] = useState(0)
  const [apiLoading, setApiLoading] = useState(false)
  const [specialities, setSpecialities] = useState<
    { code: string; display: string }[]
  >([])
  const [selectedSpeciality, setSelectedSpeciality] = useState<string>('')

  const handleDateChange = (date: Date) => {
    dispatch(setSelectedDate(date.toISOString()))
  }
  const [status, setStatus] = useState<string>(dropDownMenuKeys?.active) // Default status is 'active'
  const fetchAppointments = async () => {
    setLoading(true)
    const statusFilter = status // Determine the status filter
    if (searchValue) {
      const data = await getAppointments(
        selectedLocation,
        searchValue,
        selectedDate,
        page,
        pageSize,
        statusFilter,
        selectedSpeciality
      )

      if (data?.success) {
        const formattedData = formatData(data?.data?.entry)
        setAppointmentsData(formattedData?.length ? formattedData : [])
        setTotalCount(data?.data?.total || 0)
        setPage(0)
        setLoading(false)
      } else {
        setAppointmentsData([])
        setTotalCount(0)
        setLoading(false)
      }
    } else {
      const data = await getAppointmentsBySlotAndDateService({
        startDate,
        locationId: selectedLocation,
        limit: pageSize,
        offset: page ? page * (pageSize || 100) : 0,
        status: statusFilter,
        speciality: selectedSpeciality,
      })

      const formattedData = formatData(data?.entry)
      setAppointmentsData(formattedData?.length ? formattedData : [])
      setTotalCount(data?.total || 0)
      setLoading(false)
    }
  }
  useEffect(() => {
    fetchAppointments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startDate,
    selectedDate,
    page,
    pageSize,
    selectedLocation,
    searchValue,
    reload,
    status,
    selectedSpeciality,
  ])
  const handlePageChange = (newPage: number) => {
    setPage(newPage) // Update the page state
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize) // Update the page size
    setPage(0) // Reset to the first page when page size changes
  }
  const handleChangeStatus = async (st: string, d: AppointmentData) => {
    if (!KeycloakService.hasRole([APPOINTMENTS_WRITE])) {
      return
    }
    if (st === 'cancelled') {
      setSelectedStatus(st)
      dispatch(setSelectedAppointment(d.id))
      setShowModal(true)
    } else {
      await dispatch(
        updateAppointmentAsync({ appId: d.id, status: st, cancelReason: '' })
      )
      fetchAppointments()
    }
  }

  const handleModalCancel = async (reason: string) => {
    await dispatch(
      updateAppointmentAsync({
        appId: selectedId,
        status: selectedStatus,
        cancelReason: reason,
      })
    )
    setShowModal(false)
    fetchAppointments()
  }

  const handleModalClose = () => {
    setShowModal(false)
  }
  const handleClick = (s: string | number | null) => {
    setSelectId(s)
  }
  const deleteAppointments = async () => {
    setApiLoading(true)
    const response = await deleteAppointment(selectId)
    if (response?.success) {
      setApiLoading(false)
      toast.success(response?.message)
      setSelectId(null)
      setReload((prev) => prev + 1)
    } else {
      setApiLoading(false)
      toast.error(response?.message)
      setSelectId(null)
      setReload((prev) => prev + 1)
    }
  }
  const archiveAppointments = async () => {
    setApiLoading(true)
    const response = await ArchiveAppointment(selectId)
    if (response?.success) {
      setApiLoading(false)
      toast.success(response?.message)
      setSelectId(null)
      setReload((prev) => prev + 1)
    } else {
      setApiLoading(false)
      toast.error(response?.message)
      setSelectId(null)
      setReload((prev) => prev + 1)
    }
  }
  const handleDownload = () => {
    if (appointmentsData?.length > 0) {
      exportExcel(appointmentsData)
    } else {
      toast?.error(noDataMessage)
    }
  }
  const handleSelect = (s: any) => {
    const key = s?.[0]
    const specialityValue = s?.[0] === 'speciality' ? s?.[1] : s?.[0]
    switch (key) {
      case dropDownMenuKeys?.export:
        handleDownload()
        break
      case dropDownMenuKeys?.delete:
        deleteAppointments()
        break
      case dropDownMenuKeys?.archive:
        archiveAppointments()
        break
      case dropDownMenuKeys?.active:
        setStatus(dropDownMenuKeys?.active)
        break
      case dropDownMenuKeys?.inActive:
        setStatus(dropDownMenuKeys?.inActive)
        break
      default:
        if (specialityValue) {
          setSelectedSpeciality((prev) =>
            prev === specialityValue ? emptyString : specialityValue
          )
        }
        break
    }
  }
  const today = new Date()
  const isToday =
    startDate.getDate() === today.getDate() &&
    startDate.getMonth() === today.getMonth() &&
    startDate.getFullYear() === today.getFullYear()

  const filteredAppointmentsData = useMemo(() => {
    if (!selectedSpeciality) return appointmentsData
    return appointmentsData?.filter(
      (a) => a?.speciality?.toLowerCase() === selectedSpeciality?.toLowerCase()
    )
  }, [appointmentsData, selectedSpeciality])
  useEffect(() => {
    fetchDepartments()?.then(setSpecialities).catch(console.error)
  }, [])
  return (
    <div className='layoutcontent total-width-content'>
      {showModal && (
        <CancelModal
          handleCancel={handleModalCancel}
          handleClose={handleModalClose}
        />
      )}
      {showList === 'SlotManagement' ? (
        <Slots />
      ) : showList === 'Calender' ? (
        <Scheduler />
      ) : (
        <>
          <div className='appointments-main-container'>
            <div className='membership-dashboard-text'>{appointments}</div>
            <div className='appointments-sub-container'>
              <div className='appointments-dropdown-container'>
                <DropdownMenu
                  onSelect={handleSelect}
                  filterOptions={[
                    {
                      label: dropDownMenuKeys?.activeKey,
                      value: dropDownMenuKeys?.active,
                      active: status === dropDownMenuKeys?.active,
                    },
                    {
                      label: dropDownMenuKeys?.inActiveKey,
                      value: dropDownMenuKeys?.inActive,
                      active: status === dropDownMenuKeys?.inActive,
                    },
                    {
                      label: dateFilterKey,
                      value: dateFilterValue,
                      customRender: (
                        <div className='handleCal inline-block'>
                          <img alt={'Calendar'} src={Calendar} />
                          <DatePicker
                            className='datePickerUI'
                            placeholderText='DD-MM-YYYY'
                            selected={startDate}
                            onChange={handleDateChange}
                            dateFormat='dd-MM-yyyy'
                          />
                        </div>
                      ),
                      active: !isToday,
                    },
                    ...(specialities?.length > 0
                      ? [
                          {
                            label: 'Speciality',
                            value: 'speciality',
                            active: !!selectedSpeciality,
                            subOptions: specialities?.map((sp) => ({
                              label: sp?.display,
                              value: sp?.display,
                              active: selectedSpeciality === sp?.display,
                            })),
                          },
                        ]
                      : []),
                  ]}
                  actionOptions={[
                    {
                      label: dropDownMenuKeys?.exportKey,
                      value: dropDownMenuKeys?.export,
                    },
                    {
                      label: dropDownMenuKeys?.deleteKey,
                      value: dropDownMenuKeys?.delete,
                      disabled: !selectId,
                    },
                    {
                      label: dropDownMenuKeys?.activeKey,
                      value: dropDownMenuKeys?.archive,
                      disabled: !selectId,
                    },
                  ]}
                  selectedId={selectId ? true : false}
                  filterApply={!isToday || status !== dropDownMenuKeys?.active}
                />
              </div>
              <div>
                <AppointmentsFilter
                  startDate={startDate}
                  onChange={handleDateChange}
                  rowData={appointmentsData}
                />
              </div>
            </div>
          </div>
          <AppointmentsTable
            appointmentsData={filteredAppointmentsData}
            handleChange={handleChangeStatus}
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={handlePageChange} // Pass the page change handler
            onPageSizeChange={handlePageSizeChange} // Pass the page size change handler
            // onPageChange={setPage}
            // onPageSizeChange={setPageSize}
            loading={loading || apiLoading}
            onSelect={handleClick}
            reload={reload}
          />
        </>
      )}
    </div>
  )
}

export default Appointments
